import React, { useState } from 'react';
import { NofeeNavLogo } from '../components/NoFeeLogo';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { LogIn, Eye, EyeOff, ArrowLeft, Mail, Loader2, MailCheck } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // iter148 — inline magic-link (forgot-password) flow.
  // States: 'password' (default) | 'magic-request' | 'magic-sent'
  const [view, setView] = useState('password');
  const [mlEmail, setMlEmail] = useState('');
  const [mlSubmitting, setMlSubmitting] = useState(false);
  const [mlError, setMlError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(form.email, form.password);
      void result;
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || t('login.invalid_credentials'));
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    setMlError('');
    const email = (mlEmail || '').trim().toLowerCase();
    if (!email || !email.includes('@')) {
      setMlError('Enter a valid email to receive your sign-in link.');
      return;
    }
    setMlSubmitting(true);
    try {
      await axios.post(`${API}/auth/magic-link`, { email });
      setView('magic-sent');
    } catch (err) {
      setMlError(err?.response?.data?.detail || 'Could not send your sign-in link. Try again.');
    } finally {
      setMlSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-white/50 hover:text-white transition-colors" data-testid="login-back-home">
            <ArrowLeft className="w-4 h-4" /> {t('common.back_to_home')}
          </Link>
          <LanguageSwitcher variant="dark" compact />
        </div>
        <div className="bg-neutral-900 rounded-2xl shadow-2xl shadow-black/40 border border-white/10 p-8">
          <div className="mb-2">
            <NofeeNavLogo height={36} variant="light" />
          </div>
          <h1 className="text-2xl font-bold text-white mt-6 mb-1">{t('login.title')}</h1>
          <p className="text-white/50 mb-8">{t('login.subtitle')}</p>
          {error && <div className="p-3 bg-red-500/10 text-red-300 text-sm rounded-xl mb-6 border border-red-500/30" data-testid="login-error">{error}</div>}
          {view === 'password' && (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1.5">{t('login.email')}</label>
                <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/10 text-white placeholder:text-white/30 focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 outline-none transition-all"
                  placeholder="you@restaurant.com" data-testid="login-email" />
              </div>
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1.5">{t('login.password')}</label>
                <div className="relative">
                  <input type={showPw ? 'text' : 'password'} required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/10 text-white placeholder:text-white/30 focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 outline-none transition-all pr-12"
                    placeholder="••••••••" data-testid="login-password" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70">
                    {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <div className="mt-2 text-right">
                  <button
                    type="button"
                    onClick={() => { setMlEmail(form.email); setMlError(''); setView('magic-request'); }}
                    className="text-xs text-gold-400 hover:text-gold-300 font-medium"
                    data-testid="login-forgot-password"
                  >
                    Forgot password?
                  </button>
                </div>
              </div>
              <button type="submit" disabled={loading}
                className="w-full py-3 bg-gold-500 hover:bg-gold-400 text-black font-bold rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="login-submit">
                <LogIn className="w-5 h-5" /> {loading ? t('login.submitting') : t('login.submit')}
              </button>
            </form>
          )}
          {view === 'magic-request' && (
            <form onSubmit={handleMagicLink} className="space-y-5" data-testid="login-magic-form">
              <div>
                <h2 className="text-white text-lg font-bold mb-1">Send me a sign-in link</h2>
                <p className="text-white/50 text-sm">We&apos;ll email a single-use link that signs you in — no password needed. Valid for 15 minutes.</p>
              </div>
              {mlError && <div className="p-3 bg-red-500/10 text-red-300 text-sm rounded-xl border border-red-500/30">{mlError}</div>}
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1.5">{t('login.email')}</label>
                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                  <input
                    type="email" required autoFocus value={mlEmail}
                    onChange={(e) => setMlEmail(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 rounded-xl bg-black/40 border border-white/10 text-white placeholder:text-white/30 focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 outline-none transition-all"
                    placeholder="you@restaurant.com" data-testid="login-magic-email"
                  />
                </div>
              </div>
              <button
                type="submit" disabled={mlSubmitting}
                className="w-full py-3 bg-gold-500 hover:bg-gold-400 text-black font-bold rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="login-magic-submit"
              >
                {mlSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Mail className="w-5 h-5" />}
                {mlSubmitting ? 'Sending…' : 'Send sign-in link'}
              </button>
              <button
                type="button" onClick={() => setView('password')}
                className="w-full text-center text-sm text-white/50 hover:text-white/80 transition-colors"
                data-testid="login-magic-back"
              >
                ← Back to password sign-in
              </button>
            </form>
          )}
          {view === 'magic-sent' && (
            <div className="text-center py-3" data-testid="login-magic-sent">
              <div className="mx-auto w-14 h-14 rounded-2xl bg-emerald-500/[0.10] border border-emerald-500/30 flex items-center justify-center mb-4">
                <MailCheck className="w-7 h-7 text-emerald-300" strokeWidth={1.75} />
              </div>
              <h3 className="text-white text-lg font-bold tracking-tight">Check your inbox</h3>
              <p className="mt-2 text-sm text-white/60 leading-relaxed">
                If a restaurant account exists for <span className="text-white/80 font-medium">{mlEmail}</span>, we&apos;ve sent a single-use sign-in link. It&apos;s valid for 15 minutes.
              </p>
              <button
                type="button" onClick={() => setView('password')}
                className="mt-6 text-sm text-gold-400 hover:text-gold-300 font-medium"
                data-testid="login-magic-back-to-password"
              >
                ← Back to password sign-in
              </button>
            </div>
          )}
          <p className="text-center text-sm text-white/50 mt-6">
            {t('login.no_account')}{' '}
            <Link to="/register" className="text-gold-400 font-semibold hover:text-gold-300">{t('login.register_here')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
